import type { AlloyLegacyThemeDesignTokens, InputDesignTokens } from '../types';
import type { Weave3InputInput } from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getInputTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): InputDesignTokens => ({
  minHeight: '36px',
  borderTopLeftRadius: `${theme.borderRadius.small}px`,
  borderTopRightRadius: `${theme.borderRadius.small}px`,
  borderBottomLeftRadius: `${theme.borderRadius.small}px`,
  borderBottomRightRadius: `${theme.borderRadius.small}px`,
  borderBottomColor: {
    focus: theme.tokens.colors.border.focused.value,
    // same as default - no visual change
    hover: theme.tokens.colors.border.value,
  },
  pressedBorderColor: theme.tokens.colors.border.pressed.value,
  placeholder: {
    color: theme.tokens.colors.text.tertiary.value,
  },
  box: {
    backgroundColor: theme.tokens.colors.background.primary.value,
    borderColor: theme.tokens.colors.border.value,
    borderStyle: 'solid',
    borderWidth: '1px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingTop: '8px',
  },
  error: {
    borderBottomColor: theme.tokens.colors.border.error.value,
    icon: {
      fill: theme.tokens.colors.text.error.value,
    },
  },
  fontFamily: theme.typography.labelMedium.fontFamily,
  fontWeight: theme.typography.labelMedium.fontWeight,
  fontSize: theme.typography.labelMedium.fontSize,
  lineHeight: theme.typography.labelMedium.lineHeight,
  color: theme.tokens.colors.text.primary.value,
  opacity: {
    disabled: 0.4,
  },
  transitionDuration,
  transitionProperty: {
    border: 'border',
    'box-shadow': 'box-shadow',
    'transition-property': 'border, box-shadow',
  },
  transitionTimingFunction: transitionEasing,
  disabled: {
    backgroundColor: theme.tokens.colors.background.neutral.inactive.value,
    color: theme.tokens.colors.text.inactive.value,
    borderColor: theme.tokens.colors.border.inactive.value,
  },
  numericSpinnerIconColor: theme.colors.charcoal900,
  numericContainerWidth: '84px',
  numericContainerPaddingRight: '8px',
  numericContainerPaddingLeft: '12px',
  errorBorderColor: theme.tokens.colors.border.error.value,
});

export const getInputTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): InputDesignTokens => {
  const input = tokens.component.input;

  return {
    minHeight: input['min-height'],
    borderTopLeftRadius: input['border-top-left-radius'],
    borderTopRightRadius: input['border-top-right-radius'],
    borderBottomLeftRadius: input['border-bottom-left-radius'],
    borderBottomRightRadius: input['border-bottom-right-radius'],
    borderBottomColor: {
      focus: input['border-bottom-color']?.focus,
      hover: input['border-bottom-color']?.hover,
    },
    borderBottomStyle: input['border-bottom-style'],
    borderBottomWidth: input['border-bottom-width'],
    placeholder: { color: input.placeholder?.['text-color'] },
    box: {
      backgroundColor: input.box?.['background-color'],
      borderColor: input.box?.['border-color'],
      borderStyle: input.box?.['border-style'],
      borderWidth: input.box?.['border-width'],
      paddingTop: input.box?.['padding-top'],
      paddingRight: input.box?.['padding-right'],
      paddingBottom: input.box?.['padding-bottom'],
      paddingLeft: input.box?.['padding-left'],
    },
    boxShadow: {
      hover: input['box-shadow']?.hover,
      focus: input['box-shadow']?.focus,
    },
    backgroundColor: { selected: input['background-color']?.selected },
    error: {
      borderBottomColor: input.error?.['border-bottom-color'],
      backgroundColor: {
        selected: input.error?.['background-color']?.selected,
      },
      icon: { fill: input.error?.icon?.fill },
    },
    fontFamily,
    fontWeight: input['font-weight'],
    fontSize: input['font-size'],
    lineHeight: input['line-height'],
    color: input['text-color'],
    opacity: { disabled: input.opacity?.disabled },
    transitionDuration: input['transition-duration'],
    transitionProperty: input['transition-property'],
    transitionTimingFunction: input['transition-timing-function'],
    disabled: {
      backgroundColor: tokens?.generic.color.charcoal[100][100],
      color: tokens?.generic.color.charcoal[500][100],
      borderColor: tokens?.generic.color.charcoal[200][100],
    },
    numericSpinnerIconColor: tokens.semantic?.['icon-color']?.heavy,
    numericContainerWidth: '94px',
    numericContainerPaddingRight: '4px',
    numericContainerPaddingLeft: '12px',
    errorBorderColor: input.box?.['border-color'],
  };
};

/**
 * Weave 3 models input state as layered box-shadow rings — an inner accent ring
 * plus an outer ring in the surface colour — where Alloy and Weave 2 recolour
 * the bottom border. The rings are composed here into the single `box-shadow`
 * slot the component already reads, so no state map for `border-bottom-color`
 * is emitted: W3 has none, and the box carries no visible border to recolour
 * (DSGN-2598).
 *
 * Two deliberate departures from a literal token read:
 *
 * - `box.borderWidth` takes W3's `error.border-width` rather than its `0px`
 *   base, because the component draws the error state as a border colour on the
 *   same box. The base `border-color` is fully transparent in every W3 theme, so
 *   a border-box 1px edge is invisible until the error state colours it.
 * - `disabled` is required by {@link InputDesignTokens} but only read on the
 *   Alloy path; W3 dims via `opacity`, exactly as the Weave path already does.
 *   It is filled from the nearest W3 equivalents rather than left undefined.
 *
 * W3's `background-color.hover` has no slot in the current shape — the
 * component changes only shadow and border on hover — so hover renders as the
 * base surface. That is a component-level gap, not a token one.
 */
export const getInputTokensWeave3 = (
  input?: Weave3InputInput,
  fontFamily = 'Artifakt Element, sans-serif',
): InputDesignTokens => {
  const ring = (state: 'hover' | 'focus'): string => {
    const inner = input?.['box-shadow-color']?.[state];
    const innerSpread = input?.['box-shadow-spread']?.[state] ?? '0px';
    const outer = input?.['box-shadow-color']?.outer?.[state];
    const outerSpread = input?.['box-shadow-spread']?.outer?.[state] ?? '0px';
    const layers = [
      outer && `0 0 0 ${outerSpread} ${outer}`,
      inner && `0 0 0 ${innerSpread} ${inner}`,
    ].filter(Boolean);
    return layers.length ? layers.join(', ') : 'none';
  };

  const disabledOpacity = input?.opacity?.disabled;

  return {
    minHeight: input?.['min-height'],
    borderTopLeftRadius: input?.['border-top-left-radius'],
    borderTopRightRadius: input?.['border-top-right-radius'],
    borderBottomLeftRadius: input?.['border-bottom-left-radius'],
    borderBottomRightRadius: input?.['border-bottom-right-radius'],
    fontFamily,
    fontWeight: input?.['font-weight'],
    fontSize: input?.['font-size'],
    lineHeight: input?.['line-height'],
    color: input?.['text-color'],
    placeholder: {
      color: input?.placeholder?.['text-color'],
    },
    box: {
      backgroundColor: input?.box?.['background-color'],
      borderColor: input?.box?.['border-color']?.$root,
      borderStyle: input?.box?.['border-style'],
      borderWidth: input?.error?.['border-width'],
      paddingTop: input?.box?.['padding-top'],
      paddingRight: input?.box?.['padding-right'],
      paddingBottom: input?.box?.['padding-bottom'],
      paddingLeft: input?.box?.['padding-left'],
    },
    boxShadow: {
      hover: ring('hover'),
      focus: ring('focus'),
    },
    backgroundColor: {
      selected: input?.['background-color']?.selected,
    },
    error: {
      borderBottomColor: input?.error?.['border-color'],
      backgroundColor: {
        selected: input?.error?.['background-color']?.['active-selected'],
      },
      icon: {
        fill: input?.error?.icon?.fill,
      },
    },
    opacity: {
      disabled: disabledOpacity
        ? Number.parseFloat(disabledOpacity) / 100
        : undefined,
    },
    disabled: {
      backgroundColor: input?.['background-color']?.disabled,
      color: input?.['text-color'],
      borderColor: input?.box?.['border-color']?.$root,
    },
  };
};
